// built-from: /knowledge-retrieval v1 impl:knowledge-retrieval role:implementation
// Pure shaping for the pages a consultant owns (093). Two jobs, no I/O and no
// React: split a page ArcPilot keeps current from the notes written under it,
// and suggest a title for a reply the consultant wants to keep.

// The heading the projection writes and preserves verbatim. Everything above
// it is regenerated as the engagement changes; everything below it is the
// consultant's.
export const NOTES_HEADING = '## Your notes';

const NOTES_HEADING_LINE = /^[ \t]*##[ \t]+Your notes[ \t]*$/m;

export interface PageParts {
  generated: string;
  notes: string;
}

// Split a whole page at the notes heading. A page without the heading is all
// generated and has no notes, so a reply from an older server still reads.
export function splitPageNotes(content: string): PageParts {
  const text = typeof content === 'string' ? content : '';
  const match = NOTES_HEADING_LINE.exec(text);
  if (!match) return { generated: text.replace(/\s+$/, ''), notes: '' };
  const generated = text.slice(0, match.index).replace(/\s+$/, '');
  const notes = text
    .slice(match.index + match[0].length)
    .replace(/^\r?\n/, '')
    .replace(/^(\r?\n)+/, '')
    .replace(/\s+$/, '');
  return { generated, notes };
}

const MAX_TITLE_LENGTH = 60;

// The title offered when the consultant saves a reply as a page: its first
// line, without the heading mark, cut to a length that reads as a title.
export function pageTitleFromReply(reply: string): string {
  const firstLine = (typeof reply === 'string' ? reply : '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line.length > 0) || '';
  const plain = firstLine.replace(/^#+[ \t]*/, '').replace(/\s+/g, ' ').trim();
  return plain.slice(0, MAX_TITLE_LENGTH).trim();
}

// A page the consultant filed, rather than one the projection maintains. Only
// these can be edited whole and removed.
export function isAuthoredNotePath(path: string): boolean {
  return typeof path === 'string' && path.startsWith('wiki/notes/');
}

// The slug the removal route addresses: the file name without its extension.
export function notePageSlug(path: string): string {
  const name = (typeof path === 'string' ? path : '').split('/').filter(Boolean).slice(-1)[0] || '';
  return name.replace(/\.md$/i, '');
}
